// Cross-plan propagation for shared overlay derivedContexts + skills.
//
// When the user renames an overlay comp (or its overlay skills), adds
// a new skill under a linked overlay comp, or deletes a skill from a
// linked overlay comp, every OTHER plan in the roadmap that references
// the same id should pick up the change so the displays + skill rows
// stay in lockstep across the session. The helpers below compute the
// per-plan patch from the just-saved source payload + each candidate
// target's payload, returning `null` when nothing actually changed.
//
// Scope decisions (per product spec):
//   - Renames, skill-adds, AND skill-deletes propagate (delete is gated
//     by a confirmation modal in the UI before it lands in source).
//   - Linking is per-id; we don't propagate catalog-comp display
//     overrides (`comp_overrides`) — those stay per-plan.
//   - Skill-adds appear in the target plans with NO requirements /
//     ratings / function_assignments, matching "leave it blank in the
//     prior projects/team plans."
//   - Skill-deletes drop the row from added_skills AND scrub any stale
//     references in the target's requirements / deprecated / ratings /
//     function_assignments so the payload stays coherent.
//   - Comp-deletes do NOT propagate — the link breaks at the deleted
//     plan, other plans keep the comp.

import type {
  ProjectPlanPayload,
  TeamChangePlanPayload,
} from '@lib/api-types/plans';

import { reqKeySkillId } from './req-key.ts';

/** Subset of a plan payload that carries the overlay fields we mutate. */
interface OverlaySource {
  added_derived_contexts: Array<{ id: string; display: string }>;
  added_skills: Record<string, Array<{ id: string; display: string }>>;
}

/**
 * Compute the new `(added_derived_contexts, added_skills)` for `target`
 * after applying any rename / skill-add the user made in `source`.
 * Returns `null` if the target needs no patch.
 */
/**
 * Patch returned by `computeLinkedPatch`. `deletedSkillsByComp` is the
 * set of skill ids that were stripped from the target's added_skills
 * because the source dropped them under a linked comp — used by the
 * apply helpers to scrub stale references in requirements / ratings /
 * function_assignments.
 */
export interface LinkedPatch {
  added_derived_contexts: OverlaySource['added_derived_contexts'];
  added_skills: OverlaySource['added_skills'];
  deletedSkillsByComp: Record<string, Set<string>>;
}

export function computeLinkedPatch(
  source: OverlaySource,
  target: OverlaySource,
): LinkedPatch | null {
  // ---- Comp renames ----
  const sourceCompById = new Map(
    source.added_derived_contexts.map(c => [c.id, c]),
  );
  let compsChanged = false;
  const nextComps = target.added_derived_contexts.map(c => {
    const fresh = sourceCompById.get(c.id);
    if (!fresh || fresh.display === c.display) return c;
    compsChanged = true;
    return { id: c.id, display: fresh.display };
  });

  // ---- Skill renames + adds + deletes, gated to comps in target ----
  // Only the overlay comps the target already links to are eligible
  // for propagation; we don't introduce a brand-new comp into the
  // target plan just because the source has it. (Linking happens via
  // the OverlayPicker, not via side-effect.) Same gate applies to
  // delete propagation — the comp itself stays on the target.
  const targetCompIds = new Set(target.added_derived_contexts.map(c => c.id));
  // Comp must also exist on the source for delete propagation to
  // fire — otherwise the comp was deleted from source (not propagated
  // per spec) and any "missing" skills are a side-effect of the
  // delete, not an intentional skill-delete.
  const sourceCompIds = new Set(source.added_derived_contexts.map(c => c.id));
  const linkedCompIds = new Set(
    [...targetCompIds].filter(id => sourceCompIds.has(id)),
  );

  let skillsChanged = false;
  const nextSkills: OverlaySource['added_skills'] = {};
  for (const [compId, list] of Object.entries(target.added_skills)) {
    nextSkills[compId] = [...list];
  }
  // Deletes propagated by ids removed under linked comps.
  const deletedSkillsByComp: Record<string, Set<string>> = {};
  for (const compId of linkedCompIds) {
    const sourceSkillIds = new Set(
      (source.added_skills[compId] ?? []).map(s => s.id),
    );
    const targetSkills = nextSkills[compId] ?? [];
    const kept = targetSkills.filter(s => sourceSkillIds.has(s.id));
    if (kept.length !== targetSkills.length) {
      deletedSkillsByComp[compId] = new Set(
        targetSkills.filter(s => !sourceSkillIds.has(s.id)).map(s => s.id),
      );
      nextSkills[compId] = kept;
      skillsChanged = true;
    }
  }
  for (const [compId, sourceSkills] of Object.entries(source.added_skills)) {
    if (!linkedCompIds.has(compId)) continue;
    const existing = nextSkills[compId] ?? [];
    const byId = new Map(existing.map(s => [s.id, s]));
    for (const ss of sourceSkills) {
      const cur = byId.get(ss.id);
      if (cur) {
        if (cur.display !== ss.display) {
          byId.set(ss.id, { id: ss.id, display: ss.display });
          skillsChanged = true;
        }
      } else {
        // Add: skill exists in source under this linked comp but not
        // in target — append it (spec: leave it blank in prior plans,
        // i.e. no requirements / ratings / fn assignments touched).
        byId.set(ss.id, { id: ss.id, display: ss.display });
        skillsChanged = true;
      }
    }
    nextSkills[compId] = Array.from(byId.values());
  }

  if (!compsChanged && !skillsChanged) return null;
  return {
    added_derived_contexts: nextComps,
    added_skills: nextSkills,
    deletedSkillsByComp,
  };
}

/** Flatten the per-comp delete map to a Set of skill ids. */
function flattenDeleted(
  map: Record<string, Set<string>>,
): Set<string> {
  const out = new Set<string>();
  for (const set of Object.values(map)) for (const id of set) out.add(id);
  return out;
}

/**
 * Drop entries keyed by skill id from a `{ role|engId: { key: V } }` map.
 * Project payloads key the inner map by composite `comp::skill` while
 * team-change payloads key it by bare skill id; `reqKeySkillId` resolves the
 * skill-id portion for both, so a deleted skill is scrubbed under every
 * derivedContext it appears in.
 */
function scrubBySkillId<T>(
  map: Record<string, Record<string, T>>,
  deleted: Set<string>,
): Record<string, Record<string, T>> {
  if (deleted.size === 0) return map;
  const out: Record<string, Record<string, T>> = {};
  for (const [outerKey, inner] of Object.entries(map)) {
    const filtered: Record<string, T> = {};
    for (const [key, val] of Object.entries(inner)) {
      if (deleted.has(reqKeySkillId(key))) continue;
      filtered[key] = val;
    }
    out[outerKey] = filtered;
  }
  return out;
}

/**
 * Apply a patch to a project payload. Stale references to deleted
 * skills are scrubbed from `requirements`, `deprecated`, and
 * `function_assignments` so the payload stays coherent (an orphan
 * requirement on a deleted skill would otherwise re-introduce the
 * skill the next time impactGrid is computed).
 */
export function applyPatchToProjectPayload(
  target: ProjectPlanPayload,
  patch: LinkedPatch,
): ProjectPlanPayload {
  const deleted = flattenDeleted(patch.deletedSkillsByComp);
  return {
    ...target,
    added_derived_contexts: patch.added_derived_contexts,
    added_skills: patch.added_skills,
    requirements: scrubBySkillId(target.requirements, deleted),
    deprecated: scrubBySkillId(target.deprecated, deleted),
    function_assignments: scrubBySkillId(
      target.function_assignments,
      deleted,
    ),
  };
}

/**
 * Apply a patch to a team-change payload. Same idea — drop any
 * (engineer, skill) entries in `ratings` + `function_assignments` for
 * skills that just got removed from a linked overlay comp.
 */
export function applyPatchToTeamChangePayload(
  target: TeamChangePlanPayload,
  patch: LinkedPatch,
): TeamChangePlanPayload {
  const deleted = flattenDeleted(patch.deletedSkillsByComp);
  return {
    ...target,
    added_derived_contexts: patch.added_derived_contexts,
    added_skills: patch.added_skills,
    ratings: scrubBySkillId(target.ratings, deleted),
    function_assignments: scrubBySkillId(
      target.function_assignments,
      deleted,
    ),
  };
}
